<script setup lang="ts">
import { sortBy, uniqBy } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { navigationActions } from "@/action/navigationActions";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import type { BoardType } from "@/model/baseTypes";
import { boardIcon, boardNavName, boardTypeName } from "@/model/baseTypes";
import { assureBoardType, isArtBoard, isTeamBoard } from "@/model/board";
import type { Icon } from "@/model/icon";
import type { Art, Team } from "@/model/session";
import { useArtStore } from "@/store/art";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useFlexStore } from "@/store/flex";
import { useNavigationStore } from "@/store/navigation";
import { useSessionStore } from "@/store/session";
import { useTeamStore } from "@/store/team";
import { useUserStore } from "@/store/user";

import BreadCrumbSelector from "./BreadCrumbSelector.vue";

const { t } = useI18n();

const currentSession = computed(() => useSessionStore().current);

const currentFlex = computed(() => assureBoardType(useBoardStore().currentBoard(), "flex"));
const flexTypes = computed(() => {
  return sortBy(
    uniqBy(
      useFlexStore().flexBoards.map((flex) => flex.flexType),
      (type) => type.id,
    ),
    (type) => type.name,
  );
});
const flexes = computed(() => {
  const flexType = currentFlex.value?.flexType.id;
  return flexType ? useFlexStore().flexBoardsByType(flexType) : [];
});

function selectFlexType(type: { id: string }) {
  const flexId = useNavigationStore().lastSelectedFlexId(type.id);
  // flexId might have been deleted
  const flexById = flexId ? useFlexStore().flexBoardById(flexId) : undefined;
  const flex = flexById ?? useFlexStore().flexBoardsByType(type.id)[0];
  void navigationActions.changeCanvas("topbar-menu", flex.name);
}

function selectFlex(flex: { name: string }) {
  void navigationActions.changeCanvas("topbar-menu", flex.name);
}

const userTeams = computed(() => useUserStore().technicalUser.teams);

const teams = computed(() =>
  useTeamStore().teamsInCurrentArt.map(mark(isUserTeam, "persona/user")),
);
const currentTeam = computed(() => useTeamStore().current);

function selectTeam(team: Team) {
  navigationActions.changeTeam("topbar-menu", team);
}

const arts = computed(() => useArtStore().arts.map(mark(isUserArt, "persona/user")));
const currentArt = computed(() => useArtStore().current);
const hasArtSelector = computed(() => {
  const board = currentBoard.value.id;
  return (
    useArtStore().isMultiArt && currentArt.value.name && (isArtBoard(board) || isTeamBoard(board))
  );
});
const numberOfItems = computed(
  () =>
    1 + // Board Selector
    (hasArtSelector.value ? 1 : 0) + // ART Selector
    (isTeamBoard(currentBoard.value.id) ? 1 : 0) + // Team Selector
    (currentFlex.value ? 2 : 0), // Canvas Type and Board
);

function selectArt(art: Art) {
  navigationActions.changeArt("topbar-menu", art);
}

const boardTypes = computed(
  () =>
    [
      ...(useBoardsStore().hasSolutionBacklogBoard ? ["solution_backlog" as BoardType] : []),
      ...(useBoardsStore().hasSolutionBoard ? ["solution" as BoardType] : []),
      "backlog",
      "program",
      "objectives",
      "risk",
      "team",
      "flex",
    ] as const,
);
const boards = computed(() => boardTypes.value.map(makeBoardItem));
const currentBoard = computed(() => makeCurrentBoardItem(useBoardStore().currentBoard().type));

function selectBoard(board: { id: string }) {
  navigationActions.changeBoard("topbar-menu", board.id as BoardType);
}

function makeBoardItem(type: BoardType) {
  return { id: type, name: t(boardTypeName(type)), icon: boardIcon(type) };
}

function makeCurrentBoardItem(type: BoardType) {
  return {
    id: type,
    name: t(boardNavName(type)),
    tooltip: t(boardTypeName(type)),
    icon: boardIcon(type),
  };
}

function mark<T>(prop: (item: T) => boolean, icon: Icon) {
  return (item: T) => ({ ...item, mark: prop(item) ? icon : undefined });
}

function isUserTeam(team: Team) {
  return userTeams.value.some((userTeam) => userTeam.id === team.id);
}

function isUserArt(art: Art) {
  return userTeams.value.some((team) => team.artId === art.id);
}
</script>

<template>
  <div :class="`bread-crumb items-${numberOfItems}`">
    <div class="left">
      <span class="bread-crumb-selector">
        <BaseTooltip class="tooltip">
          <template #content>
            {{ $t("general.session", { name: currentSession.name }) }}
          </template>
          <!-- Using a BaseButton here because the icon sizing in IconButton doesn't match -->
          <BaseButton
            class="always-show-icon"
            variant="ghost"
            color="grey"
            icon-before="general/pi"
            aria-controls="bread-crumb-selector"
            aria-haspopup="menu"
            :aria-label="$t('general.session', { name: currentSession.name })"
            @click="navigationActions.changeSession('topbar-menu')"
          />
        </BaseTooltip>
        <MenuItemDelimiter />
      </span>
    </div>

    <div class="right">
      <BreadCrumbSelector
        first
        :icon="currentBoard.icon"
        :title="$t('general.boards')"
        :tooltip="$t('general.board', { name: currentBoard.tooltip })"
        :items="boards"
        :value="currentBoard"
        @change="selectBoard"
      />

      <BreadCrumbSelector
        v-if="hasArtSelector"
        icon="general/art"
        :title="$t('general.arts')"
        :tooltip="$t('general.art', { name: currentArt.name })"
        :items="arts"
        :value="currentArt"
        @change="selectArt"
      />

      <BreadCrumbSelector
        v-if="isTeamBoard(currentBoard.id)"
        icon="persona/team"
        :title="$t('general.teams')"
        :tooltip="$t('general.team', { name: currentTeam.name })"
        :items="teams"
        :value="currentTeam"
        @change="selectTeam"
      />

      <template v-if="currentFlex">
        <BreadCrumbSelector
          :title="$t('general.canvasTypes')"
          :tooltip="$t('general.canvasType', { name: currentFlex.flexType.name })"
          :items="flexTypes"
          :value="currentFlex.flexType"
          @change="selectFlexType"
        />

        <BreadCrumbSelector
          :title="$t('general.canvases', { type: currentFlex.flexType.name })"
          :tooltip="$t('general.canvas', { name: currentFlex.name })"
          :items="flexes"
          :value="currentFlex"
          @change="selectFlex"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/mixins/utils";

.bread-crumb {
  font-size: 14px;
  color: var(--text-secondary-color);

  & > div {
    display: flex;

    & > span {
      max-width: 15vw;
    }
  }

  .left,
  .right {
    transition: all 0.5s 0.1s;
  }

  .left {
    width: 50%;
    justify-content: flex-end;
  }

  .right {
    width: 50%;
    justify-content: flex-start;
  }

  // Adjust the positions based on the rough size of the component to keep it appearing centered
  &.items-2 {
    .left {
      width: 45%;
    }

    .right {
      width: 55%;
    }
  }

  &.items-3 {
    .left {
      width: 40%;
    }

    .right {
      width: 60%;
    }
  }

  h1 {
    @include utils.ellipsis;

    margin-left: 8px;
  }

  .select-button {
    font-weight: font.$weight_normal;
  }

  @media (width < 1280px) {
    button:not(.always-show-icon) > svg {
      display: none;
    }
  }
}
</style>
